import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "@github/relative-time-element";
import ScrollToTop from "../../../../components/scrollToTop/scrollToTop";
import "./style.css";

function BatchCartaPorte() {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const pageNumbers = [];
  const maxPageNumbersToShow = 20;

  let startPage = 1;
  let endPage = totalPages;

  if (totalPages > maxPageNumbersToShow) {
    startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
    endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);

    if (endPage - startPage < maxPageNumbersToShow) {
      startPage = endPage - maxPageNumbersToShow + 1;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const formatDateForAPI = (dateString, isStart = true) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (isStart) {
      return `${date.toISOString().split("T")[0]} 00:00:00`;
    }

    return `${date.toISOString().split("T")[0]} 23:59:59`;
  };

  const validateDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  const fetchBatchWaybills = async ({
    page = 1,
    limit = 10,
    search = "",
    startDate = "",
    endDate = "",
  } = {}) => {
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("limit", String(limit));

      if (search) {
        params.append("search", search);
      }

      if (startDate && endDate) {
        params.append("date_init", formatDateForAPI(startDate, true));
        params.append("date_finish", formatDateForAPI(endDate, false));
      }

      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/batch_waybills?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        },
      );

      if (res.data.code === 1) {
        const rows = Array.isArray(res.data.data) ? [...res.data.data] : [];

        rows.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });

        const pagination = res.data.pagination || {};
        const backendPage = Number(pagination.page) || page;
        const backendLimit = Number(pagination.limit) || limit;
        const backendTotalPages = Number(pagination.totalPages) || 0;
        const backendTotalRows = Number(pagination.total) || 0;

        setData(rows);
        setCurrentPage(backendPage);
        setDataPerPage(backendLimit);
        setTotalPages(backendTotalPages);
        setTotalRows(backendTotalRows);
        return;
      }

      setData([]);
      setTotalPages(0);
      setTotalRows(0);
    } catch (error) {
      Swal.fire("Error", "Hubo un error", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchWaybills({
      page: 1,
      limit: 10,
      search: "",
      startDate: "",
      endDate: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paginate = async (pageNumber) => {
    if (
      pageNumber < 1 ||
      pageNumber > totalPages ||
      pageNumber === currentPage
    ) {
      return;
    }

    setCurrentPage(pageNumber);

    await fetchBatchWaybills({
      page: pageNumber,
      limit: dataPerPage,
      search: searchValue,
      startDate: appliedStartDate,
      endDate: appliedEndDate,
    });
  };

  const showEntries = async (e) => {
    const newLimit = Number(e.target.value);

    setDataPerPage(newLimit);
    setCurrentPage(1);

    await fetchBatchWaybills({
      page: 1,
      limit: newLimit,
      search: searchValue,
      startDate: appliedStartDate,
      endDate: appliedEndDate,
    });
  };

  const handleSearch = async () => {
    const inputValue = searchInputValue.trim();
    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    if (hasStart && !hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de fin", "error");
      return;
    }

    if (!hasStart && hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de inicio", "error");
      return;
    }

    if (hasStart && hasEnd) {
      if (new Date(startDate) > new Date(endDate)) {
        Swal.fire(
          "Error",
          "La fecha de inicio no puede ser mayor que la de fin",
          "error",
        );
        return;
      }

      if (!validateDateRange(startDate, endDate)) {
        Swal.fire(
          "Error",
          "El rango de fechas no puede ser mayor a 31 días",
          "error",
        );
        return;
      }
    }

    if (!inputValue && !hasStart && !hasEnd) {
      Swal.fire(
        "Error",
        "Ingresa un término de búsqueda o selecciona un rango de fechas",
        "error",
      );
      return;
    }

    const nextStartDate = hasStart && hasEnd ? startDate : "";
    const nextEndDate = hasStart && hasEnd ? endDate : "";

    setSearchValue(inputValue);
    setAppliedStartDate(nextStartDate);
    setAppliedEndDate(nextEndDate);
    setCurrentPage(1);

    await fetchBatchWaybills({
      page: 1,
      limit: dataPerPage,
      search: inputValue,
      startDate: nextStartDate,
      endDate: nextEndDate,
    });
  };

  const clearDateFilter = async () => {
    setStartDate("");
    setEndDate("");
    setSearchValue("");
    setSearchInputValue("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setCurrentPage(1);

    await fetchBatchWaybills({
      page: 1,
      limit: dataPerPage,
      search: "",
      startDate: "",
      endDate: "",
    });
  };

  const formatDateHuman = (dateString) => {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    }).format(date);
  };

  return (
    <>
      <ScrollToTop />
      <div className="table-responsive">
        <div
          className="dataTables_wrapper dt-bootstrap4 no-footer"
          id="datatables_wrapper"
        >
          <div className="row justify-content-between mb-2">
            <div className="col-sm-12 col-md-2">
              <div className="dataTables_length" id="dataTables_length">
                <label className="d-flex justify-content-end">
                  <span className="mr-2 p-2">Mostrar</span>
                  <select
                    name="datatables_length"
                    aria-controls="datatables_length"
                    className="custom-select custom-select-sm form-control-sm narrow-select p-2"
                    value={dataPerPage}
                    onChange={showEntries}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ml-2 p-2">Registros</span>
                </label>
              </div>
            </div>
            <div className="col-sm-12 col-md-9">
              <div className="datatables_filter" id="datatables_filter">
                <label className="d-flex justify-content-end">
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />

                  <span className="mr-2 mt1 p-2"></span>

                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />
                  <span className="mr-2 mt1 p-2">Buscar</span>
                  <input
                    type="search"
                    id="searchInput"
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                  />
                  <button className="btn-amc-blue ml-2" onClick={handleSearch}>
                    Buscar
                  </button>
                  {(startDate || endDate) && (
                    <button
                      className="btn-amc-pink ml-2"
                      onClick={clearDateFilter}
                    >
                      Limpiar
                    </button>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <table
                className="table table-inverse table-hover table-striped table-bordered dataTable no-footer"
                role="grid"
                aria-describedby="datatables_info"
              >
                <thead>
                  <tr role="row">
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "81.2031px" }}
                    >
                      ID
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "178.203px" }}
                    >
                      AWB
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "180.766px" }}
                    >
                      Vuelo
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "343.25px" }}
                    >
                      Ruta
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "159.453px" }}
                    >
                      DDX
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "159.453px" }}
                    >
                      Envíos
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "159.453px" }}
                    >
                      Última respuesta
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "159.453px" }}
                    >
                      Creado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="11" className="text-center">
                        <div className="d-flex justify-content-center align-items-center">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only"></span>
                          </div>
                          <span className="ml-2">Cargando...</span>
                        </div>
                      </td>
                    </tr>
                  ) : data.length > 0 ? (
                    data.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.awb}</td>
                        <td>{row.no_flight}</td>
                        <td>{row.route}</td>
                        <td>{row.its_ddx}</td>
                        <td>{row.sends}</td>
                        <td>{row.log_status}</td>
                        <td>{formatDateHuman(row.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No hay datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="row">
            <nav aria-label="Page navigation example">
              <ul className="pagination">
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1 || totalRows === 0 || isLoading}
                  >
                    Anterior
                  </button>
                </li>
                {pageNumbers.map((pageNumber) => (
                  <li className="page-item" key={pageNumber}>
                    <button
                      className="page-link"
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={
                      currentPage >= totalPages || totalRows === 0 || isLoading
                    }
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}

export default BatchCartaPorte;
